<script>
  // HistoryPanel — per-tab transaction history table.
  import { keysApi } from "../api/client.js";

  let { tab } = $props();

  // Format helpers (Vietnam timezone for datetimes)
  const VN_FMT = new Intl.DateTimeFormat("vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  });
  function fmtTime(ts) {
    if (!ts) return "—";
    const d = new Date(typeof ts === "number" && ts < 1e12 ? ts * 1000 : ts);
    if (Number.isNaN(d.getTime())) return String(ts);
    return VN_FMT.format(d).replace(",", "");
  }
  function fmtNum(n) {
    if (n == null) return "—";
    return Number(n).toLocaleString("en-US");
  }
  function fmtCost(n) {
    if (n == null) return "—";
    const v = Number(n);
    return v === 0 ? "$0.00" : `$${v.toFixed(4)}`;
  }
  function fmtDuration(secs) {
    if (secs == null) return "—";
    return `${Number(secs).toFixed(1)}s`;
  }

  // Try several common field names — ShopAIKey shape varies a bit.
  function pick(obj, ...keys) {
    for (const k of keys) if (obj?.[k] != null) return obj[k];
    return undefined;
  }
  function unwrap(payload) {
    if (!payload) return { items: [], totals: {} };
    const root = payload.data ?? payload;
    const items =
      payload?.data?.items || payload?.data?.histories || payload?.data?.records ||
      payload?.items || payload?.histories || payload?.records ||
      (Array.isArray(payload?.data) ? payload.data : []) ||
      (Array.isArray(payload) ? payload : []);
    const totals = {
      totalRequests: pick(root, "total_requests", "total", "count"),
      totalTokens:   pick(root, "total_tokens", "sum_tokens"),
      totalCost:     pick(root, "total_cost",   "sum_cost"),
    };
    return { items: Array.isArray(items) ? items : [], totals };
  }

  async function loadPage(page) {
    tab.historyLoading = true;
    tab.historyError = null;
    tab.historyPage = page;
    try {
      const data = tab.isDefault
        ? await keysApi.historyDefault(page)
        : await keysApi.history(tab.keyId, page);
      tab.historyData = data;
    } catch (err) {
      tab.historyError = err.message;
    } finally {
      tab.historyLoading = false;
    }
  }

  // Auto-load page 1 on first mount IF we don't have data yet. Switching
  // tabs preserves historyData so we don't re-fetch.
  $effect(() => {
    if (!tab.historyData && !tab.historyLoading) {
      loadPage(tab.historyPage || 1);
    }
  });

  const parsed = $derived(unwrap(tab.historyData));
</script>

<div class="panel">
  <div class="head card">
    <h2>History cho key "{tab.label}"</h2>
    <button class="link-btn" onclick={() => loadPage(tab.historyPage)} disabled={tab.historyLoading}>
      {tab.historyLoading ? "Đang tải..." : "↻ Refresh"}
    </button>
  </div>

  {#if tab.historyError}
    <div class="error card">Lỗi: {tab.historyError}</div>
  {/if}

  {#if tab.historyLoading && !tab.historyData}
    <p class="muted">Đang tải lịch sử transactions...</p>
  {:else if parsed.items.length === 0}
    <p class="muted">Không có transaction nào.</p>
  {:else}
    <div class="stats card">
      <div class="stat">
        <span class="label">Tổng request</span>
        <span class="value">{fmtNum(parsed.totals.totalRequests)}</span>
      </div>
      <div class="stat">
        <span class="label">Tổng token</span>
        <span class="value">{fmtNum(parsed.totals.totalTokens)}</span>
      </div>
      <div class="stat">
        <span class="label">Tổng chi phí</span>
        <span class="value">{fmtCost(parsed.totals.totalCost)}</span>
      </div>
    </div>

    <div class="table-wrap card">
      <table>
        <thead>
          <tr>
            <th>Thời gian</th>
            <th>Model</th>
            <th class="num">IN</th>
            <th class="num">OUT</th>
            <th>Duration</th>
            <th class="num">Chi phí</th>
          </tr>
        </thead>
        <tbody>
          {#each parsed.items as item}
            <tr>
              <td>{fmtTime(pick(item, "created_at", "createdAt", "time", "timestamp"))}</td>
              <td>{pick(item, "model", "model_name") || "—"}</td>
              <td class="num">{fmtNum(pick(item, "input_tokens", "prompt_tokens", "in_tokens"))}</td>
              <td class="num">{fmtNum(pick(item, "output_tokens", "completion_tokens", "out_tokens"))}</td>
              <td>{fmtDuration(pick(item, "duration", "duration_seconds", "elapsed"))}</td>
              <td class="num">{fmtCost(pick(item, "cost", "amount", "price"))}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    <div class="pager card">
      <button class="link-btn"
              onclick={() => loadPage(tab.historyPage - 1)}
              disabled={tab.historyPage <= 1 || tab.historyLoading}>
        ← Trước
      </button>
      <span class="muted">Trang {tab.historyPage}</span>
      <button class="link-btn"
              onclick={() => loadPage(tab.historyPage + 1)}
              disabled={parsed.items.length < 20 || tab.historyLoading}>
        Sau →
      </button>
    </div>
  {/if}
</div>

<style>
  .panel { display: flex; flex-direction: column; gap: 12px; }

  .head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px;
  }
  h2 { margin: 0; font-size: 1rem; color: var(--text-strong); }
  .muted { color: var(--muted); font-size: 0.85rem; margin: 0; padding: 20px; text-align: center; }

  .error {
    padding: 12px 14px;
    background: rgba(248, 113, 113, 0.08);
    border-color: rgba(248, 113, 113, 0.3);
    color: #ff8585;
    font-size: 0.82rem;
  }

  .stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 12px;
    padding: 14px;
  }
  .stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .label {
    font-size: 0.7rem;
    text-transform: uppercase;
    color: var(--muted);
    letter-spacing: 0.04em;
  }
  .value {
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-strong);
    font-variant-numeric: tabular-nums;
  }

  .table-wrap {
    padding: 0;
    overflow: auto;
    max-height: 60vh;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.78rem;
    font-variant-numeric: tabular-nums;
  }
  th, td {
    padding: 8px 12px;
    text-align: left;
    border-bottom: 1px solid var(--border);
    white-space: nowrap;
  }
  th {
    position: sticky;
    top: 0;
    background: var(--bg-elevated);
    font-size: 0.7rem;
    text-transform: uppercase;
    color: var(--muted);
    letter-spacing: 0.04em;
    font-weight: 600;
  }
  td.num, th.num { text-align: right; }
  tbody tr:hover { background: rgba(255, 255, 255, 0.025); }

  .pager {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    padding: 12px 14px;
  }
</style>
